/** Run sheet / timeline — four time slots with descriptions and side image. */
import { cn } from '@rocco/utils/cn';
import { useInView } from 'react-intersection-observer';
import { ScrollReveal } from './components/ScrollReveal';
import { HDEDesktopFrame } from './components/HDEDesktopFrame';
import { HDEPresentedByMark } from './components/HDEPresentedByMark';

interface TimeSlotProps {
    prefix?: string;
    startTime: string;
    endTime?: string;
    startPeriod?: string;
    endPeriod?: string;
    sharedPeriod?: string;
    title: string;
    descriptions: { text?: string; html?: string; muted?: boolean }[];
    isVisible?: boolean;
    delay?: number;
}

export interface RunSheetTimeSlot {
    prefix?: string;
    startTime: string;
    endTime?: string;
    startPeriod?: string;
    endPeriod?: string;
    sharedPeriod?: string;
    title: string;
    descriptions: { text?: string; html?: string; muted?: boolean }[];
}

const TimeSlot = ({
    prefix,
    startTime,
    endTime,
    startPeriod,
    endPeriod,
    sharedPeriod,
    title,
    descriptions,
    isVisible = false,
    delay = 0,
}: TimeSlotProps) => (
    <>
        <div className="overflow-hidden">
            <div
                className={cn(
                    'h-px w-full origin-left bg-black/20',
                    isVisible && 'hde-line-draw',
                )}
                style={{ animationDelay: `${delay}ms` }}
            />
        </div>
        <ScrollReveal animation="fade-up" duration={500} delay={delay}>
            <div className="flex items-start pb-[6.2rem] pt-[1.1875rem]">
                {/* Left: time + title */}
                <div className="flex w-[18.625rem] shrink-0 flex-col gap-[0.25rem]">
                    <div className="flex items-center gap-[0.125rem]">
                        <span className="font-display text-[1.93rem] font-[450] uppercase leading-[0.9] tracking-[-0.058rem] text-black opacity-40">
                            {prefix ? `${prefix} ${startTime}` : startTime}
                        </span>
                        {startPeriod && !sharedPeriod ? (
                            <span className="mt-[0.1875rem] self-start font-display text-[1.034rem] font-[450] uppercase leading-[0.9] tracking-[-0.031rem] text-black opacity-40">
                                {startPeriod}
                            </span>
                        ) : null}
                        {endTime ? (
                            <span className="font-display text-[1.93rem] font-[450] uppercase leading-[0.9] tracking-[-0.058rem] text-black opacity-40">
                                - {endTime}
                            </span>
                        ) : null}
                        {endPeriod ? (
                            <span className="mt-[0.1875rem] self-start font-display text-[1.034rem] font-[450] uppercase leading-[0.9] tracking-[-0.031rem] text-black opacity-40">
                                {endPeriod}
                            </span>
                        ) : null}
                        {sharedPeriod ? (
                            <span className="mt-[0.1875rem] self-start font-display text-[1.034rem] font-[450] uppercase leading-[0.9] tracking-[-0.031rem] text-black opacity-40">
                                {sharedPeriod}
                            </span>
                        ) : null}
                    </div>
                    <p className="whitespace-nowrap font-display text-[1.93rem] font-bold uppercase leading-[0.9] tracking-[-0.058rem] text-black">
                        {title}
                    </p>
                </div>

                {/* Spacer */}
                <div className="w-[27.75rem] shrink-0" />

                {/* Right: description */}
                <div className="flex w-[34.4375rem] flex-col gap-[1.25rem] pr-[1.875rem]">
                    {descriptions.map((desc, i) => (
                        <div key={i}>
                            {desc.html ? (
                                <div
                                    className="font-sans text-[1.125rem] font-medium leading-[1.25] tracking-[-0.0225rem] text-[#818585] [&_p:last-child]:mb-0 [&_p]:mb-[1.25rem] [&_strong]:font-medium [&_strong]:text-black"
                                    dangerouslySetInnerHTML={{
                                        __html: desc.html.replace(
                                            /<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>/gi,
                                            '',
                                        ),
                                    }}
                                />
                            ) : (
                                <p
                                    className={`font-sans text-[1.125rem] font-medium leading-[1.25] tracking-[-0.0225rem] ${
                                        desc.muted
                                            ? 'text-[#818585]'
                                            : 'text-black'
                                    }`}
                                >
                                    {desc.text}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </ScrollReveal>
    </>
);

const defaultTimeSlots: TimeSlotProps[] = [
    {
        prefix: 'From ',
        startTime: '5:00',
        sharedPeriod: 'pm',
        title: 'Explore & Connect',
        descriptions: [
            {
                text: 'Walk the floor. Meet 60+ exhibiting brands and professionals. Scan QR codes to save everything to your ArchiPro account - products, specs and contacts - all in one place.',
            },
            {
                text: 'Browse exhibitors from kitchen and bathroom to flooring, lighting, outdoor living and more. Every exhibitor is an ArchiPro-vetted business.',
                muted: true,
            },
        ],
    },
    {
        startTime: '6:00',
        endTime: '6:45',
        sharedPeriod: 'pm',
        title: 'the Professional panel',
        descriptions: [
            {
                text: 'Join us for a session strictly for the professional community, featuring two leading Australian practices challenging the way the industry operates - from how buildings get made, to how studios get run. A conversation about what a better model looks like and what NZ practices can take from it.',
            },
        ],
    },
    {
        startTime: '7:00',
        endTime: '7:30',
        sharedPeriod: 'pm',
        title: 'THE PUBLIC PANEL',
        descriptions: [
            {
                text: "Hosted by Tom Webster - architect and host of Grand Designs New Zealand. Tom sits down with six of Australasia's most accomplished architects, designers and builders for the conversation most homeowners never get to hear: the decisions made before construction starts that determine whether a home works, what it costs and how it feels to live in.",
            },
        ],
    },
    {
        startTime: '7:30',
        endTime: '9:00',
        sharedPeriod: 'pm',
        title: 'Connect & Continue',
        descriptions: [
            {
                text: "The conversations continue. Return to exhibitors, connect with panellists and use your ArchiPro App to enquire with professionals you've met.",
            },
            {
                text: 'Drinks and canapés provided. This is the part where plans start to take shape.',
                muted: true,
            },
        ],
    },
];

interface RunSheetProps {
    sideImage: string;
    onRegister?: () => void;
    showRegisterButton?: boolean;
    timeSlots?: RunSheetTimeSlot[];
    isMobile?: boolean;
    /** When false, hides “Presented by” and sponsor mark (e.g. no active register-module event). */
    showPresentedBySponsor?: boolean;
    presentedByLogoUrl?: string | null;
    presentedByProfilePath?: string | null;
    presentedBySponsorName?: string | null;
}

export const RunSheet = ({
    sideImage,
    onRegister,
    showRegisterButton = true,
    timeSlots,
    isMobile = false,
    showPresentedBySponsor = false,
    presentedByLogoUrl,
    presentedByProfilePath,
    presentedBySponsorName,
}: RunSheetProps) => {
    const { ref: sectionRef, inView } = useInView({
        triggerOnce: true,
        threshold: 0.15,
    });

    if (timeSlots && timeSlots.length === 0) {
        return null;
    }

    const slotsToRender: TimeSlotProps[] =
        timeSlots && timeSlots.length > 0
            ? timeSlots.map((slot) => ({
                  prefix: slot.prefix,
                  startTime: slot.startTime,
                  endTime: slot.endTime,
                  startPeriod: slot.startPeriod,
                  endPeriod: slot.endPeriod,
                  sharedPeriod: slot.sharedPeriod,
                  title: slot.title,
                  descriptions: slot.descriptions,
              }))
            : defaultTimeSlots;

    if (isMobile) {
        return (
            <section
                ref={sectionRef}
                data-testid="run-sheet"
                className="relative w-full bg-white px-[1.5rem] pt-[1.5rem]"
            >
                {showPresentedBySponsor ? (
                    <div className="flex items-center gap-[0.75rem] pb-8">
                        <p className="font-sans text-[0.625rem] font-medium uppercase tracking-[0.125em] text-[#515151]">
                            Presented by
                        </p>
                        <HDEPresentedByMark
                            logoUrl={presentedByLogoUrl}
                            profilePath={presentedByProfilePath}
                            sponsorName={presentedBySponsorName}
                            className="mt-1"
                            imageClassName="max-h-[2rem] w-[9.25rem]"
                            fallbackLogoClassName="h-auto w-[9.25rem] text-[#515151]"
                        />
                    </div>
                ) : null}
                <div>
                    {slotsToRender.map((slot, index) => (
                        <div
                            key={`${slot.title}-${index}`}
                            className="border-t border-[#dbdbdb] py-[1.5625rem]"
                        >
                            <p className="font-display text-[1.125rem] uppercase leading-[1] tracking-[-0.03em] text-[#828585]">
                                {slot.prefix ? `${slot.prefix} ` : ''}
                                {slot.startTime}
                                {slot.endTime ? ` - ${slot.endTime}` : ''}
                                {slot.sharedPeriod
                                    ? ` ${slot.sharedPeriod}`
                                    : ''}
                            </p>
                            <p className="mt-[0.3125rem] font-display text-[1.125rem] font-bold uppercase leading-[1] tracking-[-0.02em] text-black">
                                {slot.title}
                            </p>
                            {slot.descriptions.map((desc, descIndex) => (
                                <div key={`${slot.title}-${descIndex}`}>
                                    {desc.html ? (
                                        <div
                                            className={`mt-[0.25rem] text-[0.75rem] font-normal leading-[1.42] [&_strong]:font-normal ${
                                                desc.muted
                                                    ? 'text-[#818585]'
                                                    : 'text-[#141414]'
                                            }`}
                                            dangerouslySetInnerHTML={{
                                                __html: desc.html,
                                            }}
                                        />
                                    ) : (
                                        <p
                                            className={`mt-[0.25rem] text-[0.75rem] font-normal leading-[1.42] ${
                                                desc.muted
                                                    ? 'text-[#818585]'
                                                    : 'text-[#141414]'
                                            }`}
                                        >
                                            {desc.text}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <div
                    className="relative aspect-[25.1875/44.8125] w-full shrink-0 overflow-hidden rounded-[0.375rem]"
                    style={{ backgroundColor: '#2a1c14' }}
                >
                    <img
                        src={sideImage}
                        alt="Tom Webster"
                        className="absolute left-0 top-0 overflow-hidden object-cover"
                        style={{
                            width: '100%',
                            height: '100%',
                        }}
                    />

                    {/* Corner dots — top-left */}
                    <div
                        className="absolute z-30 rounded-full bg-white/50"
                        style={{
                            width: '0.55rem',
                            height: '0.55rem',
                            top: '1.1rem',
                            left: '0.9rem',
                        }}
                    />
                    {/* Corner dots — top-right */}
                    <div
                        className="absolute z-30 rounded-full bg-white/50"
                        style={{
                            width: '0.55rem',
                            height: '0.55rem',
                            top: '1.1rem',
                            right: '0.9rem',
                        }}
                    />
                    {/* Corner dots — bottom-left */}
                    <div
                        className="absolute z-30 rounded-full bg-white/50"
                        style={{
                            width: '0.55rem',
                            height: '0.55rem',
                            bottom: '1.1rem',
                            left: '0.9rem',
                        }}
                    />
                    {/* Corner dots — bottom-right */}
                    <div
                        className="absolute z-30 rounded-full bg-white/50"
                        style={{
                            width: '0.55rem',
                            height: '0.55rem',
                            bottom: '1.1rem',
                            right: '0.9rem',
                        }}
                    />

                    {/* Top decorative dot strip */}
                    <div
                        className="absolute inset-x-0 top-0 z-20 overflow-hidden"
                        style={{ height: '5.26%' }}
                    >
                        <div
                            className="absolute inset-0"
                            style={{
                                background:
                                    'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 6.38%, #000 161.7%)',
                            }}
                        />
                        <div
                            className="absolute inset-0 opacity-30"
                            style={{
                                backgroundImage:
                                    'radial-gradient(circle, rgba(255,255,255,0.6) 0.7px, transparent 0.7px)',
                                backgroundSize: '4px 4px',
                            }}
                        />
                    </div>

                    {/* Bottom decorative dot strip */}
                    <div
                        className="absolute inset-x-0 bottom-0 z-20 overflow-hidden"
                        style={{ height: '5.26%' }}
                    >
                        <div
                            className="absolute inset-0"
                            style={{
                                background:
                                    'linear-gradient(0deg, rgba(0, 0, 0, 0.20) 6.38%, #000 161.7%)',
                            }}
                        />
                        <div
                            className="absolute inset-0 opacity-30"
                            style={{
                                backgroundImage:
                                    'radial-gradient(circle, rgba(255,255,255,0.6) 0.7px, transparent 0.7px)',
                                backgroundSize: '4px 4px',
                            }}
                        />
                    </div>

                    {/* HDE serif letters (outlined) */}
                    <div
                        className="absolute z-10"
                        style={{
                            left: '6%',
                            top: '4.41875rem',
                            width: '44.38%',
                            height: '32.47%',
                        }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="180"
                            height="233"
                            viewBox="0 0 180 233"
                            fill="none"
                        >
                            <path
                                d="M105.122 119.913L105.121 119.917C105.737 119.917 106.167 120.524 105.953 121.097L95.6827 148.683C95.5747 148.97 95.2976 149.162 94.9873 149.162H94.6173C94.217 149.162 93.89 148.848 93.8776 148.453C93.7459 144.148 91.791 121.68 65.2762 121.68H48.1565C44.4993 121.68 40.9515 123.133 38.5147 125.83C36.1237 128.477 34.2785 132.698 34.2784 139.42V170.022H50.7095C73.4897 170.022 76.7537 154.363 77.1028 152.131C77.1264 151.978 77.2593 151.867 77.4159 151.867H77.884C78.0682 151.867 78.2138 152.02 78.2013 152.202L75.5303 190.209C75.5192 190.374 75.3808 190.502 75.2132 190.502H74.1908C74.0204 190.502 73.8804 190.367 73.8735 190.199C73.7737 187.88 72.2941 172.998 50.8455 172.998H34.2784V206.717C34.2784 211.508 34.2575 215.322 34.4805 218.362C34.4875 218.414 34.493 218.464 34.4999 218.516C35.2702 224.583 40.2436 229.29 46.4055 229.862C51.0823 230.296 57.5642 230.256 66.5437 230.256C97.5597 230.256 102.532 203.514 102.985 200.57C103.008 200.418 103.141 200.308 103.296 200.308H103.819C104.006 200.308 104.151 200.465 104.133 200.648L101.207 231.303C101.134 232.067 100.484 232.652 99.707 232.652H2.15966C1.90619 232.652 1.70125 232.449 1.70122 232.199V231.325C1.70122 231.069 1.9117 230.864 2.16934 230.872C4.52161 230.942 14.9241 230.373 14.9241 214.81V139.418C14.9241 121.788 2.236 121.361 0.257692 121.402C0.116414 121.405 3.96727e-05 121.294 0 121.154V120.16C0 120.024 0.112196 119.913 0.250715 119.913H105.122Z"
                                fill="#D9D9D9"
                            />
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M48.9516 0C49.0901 0 49.2023 0.110856 49.2023 0.247721V1.24149C49.2023 1.38106 49.0861 1.49189 48.9448 1.48921C46.9666 1.44952 34.2785 1.87522 34.2784 19.505V53.9926H84.0792V19.505C84.0792 1.87522 71.391 1.44815 69.4128 1.48921C69.2715 1.49193 69.1551 1.38109 69.1551 1.24149V0.247721C69.1551 0.110886 69.2673 4.87455e-05 69.4058 0H115.749C145.56 0 179.015 15.9256 179.015 56.1552C179.015 96.3847 153.433 112.739 114.549 112.739H71.3163C71.0628 112.739 70.8577 112.536 70.8577 112.286V111.413C70.8577 111.157 71.0683 110.951 71.326 110.959C73.6784 111.031 84.0805 110.46 84.0805 94.897V56.3689H34.2798V94.897C34.2798 110.46 44.6819 111.029 47.0343 110.959C47.292 110.953 47.5027 111.158 47.5027 111.413V112.286C47.5027 112.536 47.2975 112.739 47.044 112.739H2.15966C1.90617 112.739 1.70122 112.536 1.70122 112.286V111.413C1.70122 111.157 1.9117 110.951 2.16934 110.959C4.52161 111.031 14.9241 110.46 14.9241 94.897V19.505C14.9241 1.87529 2.236 1.44815 0.257692 1.48921C0.116395 1.49195 9.3018e-06 1.3811 0 1.24149V0.247721C1.49214e-05 0.110865 0.112205 1.47299e-05 0.250715 0H48.9516ZM106.55 2.07783C105.424 2.07788 104.51 2.97991 104.51 4.09406V54.3171C104.51 82.9582 104.567 100.8 106.211 104.088C107.832 107.331 108.385 110.234 115.219 110.234C137.089 110.234 159.41 98.7605 159.41 56.4345C159.41 14.108 130.1 2.07785 119.277 2.07783H106.55Z"
                                fill="#D9D9D9"
                            />
                        </svg>
                    </div>

                    {/* HOME */}
                    <p
                        className="absolute z-10 whitespace-nowrap font-sans text-[1.078rem] font-medium leading-[0.9] text-white"
                        style={{ left: '64.4%', top: '49.1%' }}
                    >
                        HOME
                    </p>
                    {/* DESIGN */}
                    <p
                        className="absolute z-10 whitespace-nowrap font-sans text-[1.078rem] font-medium leading-[0.9] text-white"
                        style={{ left: '72.2%', top: '52.4%' }}
                    >
                        DESIGN
                    </p>
                    {/* EVENING */}
                    <p
                        className="absolute z-10 whitespace-nowrap font-sans text-[1.078rem] font-medium leading-[0.9] text-white"
                        style={{ left: '55.7%', top: '55.9%' }}
                    >
                        EVENING
                    </p>

                    {/* HOSTED BY */}
                    <p
                        className="absolute z-10 whitespace-nowrap font-sans text-[1.078rem] font-medium leading-[0.9] text-white"
                        style={{ left: '8.3%', top: '68.1%' }}
                    >
                        HOSTED BY
                    </p>
                    {/* TOM */}
                    <p
                        className="absolute z-10 whitespace-nowrap font-sans text-[1.078rem] font-medium leading-[1] text-white"
                        style={{ left: '28.6%', top: '71.5%' }}
                    >
                        <span>TOM</span>
                        <br />
                        <span>WEBSTER</span>
                    </p>

                    {/* BY ARCHIPRO */}
                    <p
                        className="absolute z-10 whitespace-nowrap font-sans text-[0.47rem] font-medium uppercase leading-[0.9] text-white"
                        style={{
                            left: 'calc(50% + 2.84rem)',
                            top: '89.75%',
                        }}
                    >
                        By ArchiPro
                    </p>

                    {/* ArchiPro logo */}
                    <div
                        className="absolute z-10"
                        style={{
                            top: '84.65%',
                            left: '76.46%',
                            right: '3.55%',
                            bottom: '6.75%',
                        }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="81"
                            height="60"
                            viewBox="0 0 81 60"
                            fill="none"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M80.4528 59.1523C76.8605 51.777 73.2727 44.4037 69.687 37.0369C69.6323 36.9153 69.5405 36.7723 69.3896 36.689C69.2388 36.6058 69.0704 36.5845 68.8715 36.5845H56.4134C63.3661 30.2911 67.8023 21.3877 68.0865 11.4707C68.1018 10.9137 67.6383 10.457 67.0655 10.457H56.7282C56.7282 24.4096 45.1403 35.7223 30.8435 35.7223H27.5923C31.6153 27.4485 35.6317 19.1747 39.6437 10.8966C39.6984 10.7664 39.7836 10.6298 39.9367 10.5487C40.0897 10.4676 40.2537 10.4591 40.4592 10.4591H56.7588L56.7435 10.4207L50.8927 0V0.00426815H29.3589C29.2124 0.00426815 29.0812 0.0149385 28.9654 0.0746927C28.8495 0.136581 28.7817 0.236883 28.738 0.33505C22.5789 13.0115 16.4133 25.705 10.2433 38.4155C6.88496 45.3257 3.52884 52.2379 0.170539 59.1523C0.128997 59.2292 0.0983878 59.3103 0.0634055 59.3978C0.0437279 59.449 0.021864 59.5023 0 59.5578H15.6152C15.7399 59.5685 15.8667 59.5407 15.9694 59.4853C16.0722 59.4276 16.1443 59.3423 16.1706 59.2462C18.2389 54.9717 20.3007 50.7163 22.3581 46.4781C22.4127 46.3607 22.4936 46.239 22.6336 46.1665C22.7735 46.0939 22.8959 46.0897 23.108 46.0897H57.5262C57.7405 46.0897 57.8477 46.0982 57.9832 46.1665C58.1188 46.2369 58.1931 46.3457 58.2521 46.4631C60.3139 50.6993 62.3691 54.9375 64.4156 59.1737C64.4571 59.2569 64.5315 59.385 64.6517 59.4511C64.772 59.5173 64.8791 59.5258 65.0671 59.5301H80.6233L80.4528 59.1481V59.1523Z"
                                fill="#D9D9D9"
                            />
                        </svg>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onRegister}
                    className={`mb-[2.25rem] mt-[1.5rem] flex h-[3.25rem] w-full items-center justify-center rounded-full bg-[#141414] ${showRegisterButton ? '' : 'pointer-events-none invisible'}`}
                >
                    <span className="text-[0.9375rem] text-white">
                        Register Now — Free
                    </span>
                </button>
            </section>
        );
    }

    return (
        <section
            ref={sectionRef}
            data-testid="run-sheet"
            className="relative w-full bg-white pt-[9.65rem]"
        >
            <HDEDesktopFrame>
                {showPresentedBySponsor ? (
                    <div className="flex items-start gap-[0.75rem] px-[3.125rem] pb-[2.5rem] pt-[1.15rem]">
                        <p className="mt-[1.8rem] font-sans text-[0.8125rem] font-medium uppercase tracking-[0.065em] text-[#515151]">
                            Presented by
                        </p>
                        <HDEPresentedByMark
                            logoUrl={presentedByLogoUrl}
                            profilePath={presentedByProfilePath}
                            sponsorName={presentedBySponsorName}
                            className="mt-3"
                            imageClassName="max-h-[2.75rem] w-[11.4375rem]"
                            fallbackLogoClassName="h-auto w-[11.4375rem] text-[#515151]"
                        />
                    </div>
                ) : null}
                <div className="flex items-start gap-12 px-[3.125rem]">
                    {/* Timeline column */}
                    <div className="flex w-[85rem] shrink-0 flex-col">
                        {slotsToRender.map((slot, i) => (
                            <TimeSlot
                                key={i}
                                {...slot}
                                isVisible={inView}
                                delay={i * 150}
                            />
                        ))}

                        {/* Register pill */}
                        <ScrollReveal
                            animation="fade-up"
                            duration={400}
                            delay={600}
                        >
                            <button
                                type="button"
                                onClick={onRegister}
                                className={`flex h-[3.125rem] items-center justify-center self-start rounded-full border border-black bg-[#1e1e1e] px-[2.234rem] transition-colors hover:bg-gray-700 active:scale-[0.97] ${showRegisterButton ? '' : 'pointer-events-none invisible'}`}
                            >
                                <span className="whitespace-nowrap text-[1.058rem] font-normal leading-[1.713rem] text-white">
                                    Register Now – Free
                                </span>
                            </button>
                        </ScrollReveal>
                    </div>

                    {/* Side image card */}
                    <ScrollReveal
                        animation="fade-right"
                        duration={700}
                        delay={300}
                    >
                        <div
                            className="relative shrink-0 overflow-hidden"
                            style={{
                                width: '25.1875rem',
                                height: '44.8125rem',
                                backgroundColor: '#2a1c14',
                            }}
                        >
                            <img
                                src={sideImage}
                                alt="Tom Webster"
                                className="absolute left-0 top-0 overflow-hidden rounded-[0.375rem] object-cover"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                }}
                            />

                            {/* Corner dots — top-left */}
                            <div
                                className="absolute z-30 rounded-full bg-white/50"
                                style={{
                                    width: '0.55rem',
                                    height: '0.55rem',
                                    top: '1.1rem',
                                    left: '0.9rem',
                                }}
                            />
                            {/* Corner dots — top-right */}
                            <div
                                className="absolute z-30 rounded-full bg-white/50"
                                style={{
                                    width: '0.55rem',
                                    height: '0.55rem',
                                    top: '1.1rem',
                                    right: '0.9rem',
                                }}
                            />
                            {/* Corner dots — bottom-left */}
                            <div
                                className="absolute z-30 rounded-full bg-white/50"
                                style={{
                                    width: '0.55rem',
                                    height: '0.55rem',
                                    bottom: '1.1rem',
                                    left: '0.9rem',
                                }}
                            />
                            {/* Corner dots — bottom-right */}
                            <div
                                className="absolute z-30 rounded-full bg-white/50"
                                style={{
                                    width: '0.55rem',
                                    height: '0.55rem',
                                    bottom: '1.1rem',
                                    right: '0.9rem',
                                }}
                            />

                            {/* Top decorative dot strip */}
                            <div
                                className="absolute inset-x-0 top-0 z-20 overflow-hidden"
                                style={{ height: '5.26%' }}
                            >
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background:
                                            'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 6.38%, #000 161.7%)',
                                    }}
                                />
                                <div
                                    className="absolute inset-0 opacity-30"
                                    style={{
                                        backgroundImage:
                                            'radial-gradient(circle, rgba(255,255,255,0.6) 0.7px, transparent 0.7px)',
                                        backgroundSize: '4px 4px',
                                    }}
                                />
                            </div>

                            {/* Bottom decorative dot strip */}
                            <div
                                className="absolute inset-x-0 bottom-0 z-20 overflow-hidden"
                                style={{ height: '5.26%' }}
                            >
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background:
                                            'linear-gradient(0deg, rgba(0, 0, 0, 0.20) 6.38%, #000 161.7%)',
                                    }}
                                />
                                <div
                                    className="absolute inset-0 opacity-30"
                                    style={{
                                        backgroundImage:
                                            'radial-gradient(circle, rgba(255,255,255,0.6) 0.7px, transparent 0.7px)',
                                        backgroundSize: '4px 4px',
                                    }}
                                />
                            </div>

                            {/* HDE serif letters (outlined) */}
                            <div
                                className="absolute z-10"
                                style={{
                                    left: '6%',
                                    top: '4.41875rem',
                                    width: '44.38%',
                                    height: '32.47%',
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="180"
                                    height="233"
                                    viewBox="0 0 180 233"
                                    fill="none"
                                >
                                    <path
                                        d="M105.122 119.913L105.121 119.917C105.737 119.917 106.167 120.524 105.953 121.097L95.6827 148.683C95.5747 148.97 95.2976 149.162 94.9873 149.162H94.6173C94.217 149.162 93.89 148.848 93.8776 148.453C93.7459 144.148 91.791 121.68 65.2762 121.68H48.1565C44.4993 121.68 40.9515 123.133 38.5147 125.83C36.1237 128.477 34.2785 132.698 34.2784 139.42V170.022H50.7095C73.4897 170.022 76.7537 154.363 77.1028 152.131C77.1264 151.978 77.2593 151.867 77.4159 151.867H77.884C78.0682 151.867 78.2138 152.02 78.2013 152.202L75.5303 190.209C75.5192 190.374 75.3808 190.502 75.2132 190.502H74.1908C74.0204 190.502 73.8804 190.367 73.8735 190.199C73.7737 187.88 72.2941 172.998 50.8455 172.998H34.2784V206.717C34.2784 211.508 34.2575 215.322 34.4805 218.362C34.4875 218.414 34.493 218.464 34.4999 218.516C35.2702 224.583 40.2436 229.29 46.4055 229.862C51.0823 230.296 57.5642 230.256 66.5437 230.256C97.5597 230.256 102.532 203.514 102.985 200.57C103.008 200.418 103.141 200.308 103.296 200.308H103.819C104.006 200.308 104.151 200.465 104.133 200.648L101.207 231.303C101.134 232.067 100.484 232.652 99.707 232.652H2.15966C1.90619 232.652 1.70125 232.449 1.70122 232.199V231.325C1.70122 231.069 1.9117 230.864 2.16934 230.872C4.52161 230.942 14.9241 230.373 14.9241 214.81V139.418C14.9241 121.788 2.236 121.361 0.257692 121.402C0.116414 121.405 3.96727e-05 121.294 0 121.154V120.16C0 120.024 0.112196 119.913 0.250715 119.913H105.122Z"
                                        fill="#D9D9D9"
                                    />
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M48.9516 0C49.0901 0 49.2023 0.110856 49.2023 0.247721V1.24149C49.2023 1.38106 49.0861 1.49189 48.9448 1.48921C46.9666 1.44952 34.2785 1.87522 34.2784 19.505V53.9926H84.0792V19.505C84.0792 1.87522 71.391 1.44815 69.4128 1.48921C69.2715 1.49193 69.1551 1.38109 69.1551 1.24149V0.247721C69.1551 0.110886 69.2673 4.87455e-05 69.4058 0H115.749C145.56 0 179.015 15.9256 179.015 56.1552C179.015 96.3847 153.433 112.739 114.549 112.739H71.3163C71.0628 112.739 70.8577 112.536 70.8577 112.286V111.413C70.8577 111.157 71.0683 110.951 71.326 110.959C73.6784 111.031 84.0805 110.46 84.0805 94.897V56.3689H34.2798V94.897C34.2798 110.46 44.6819 111.029 47.0343 110.959C47.292 110.953 47.5027 111.158 47.5027 111.413V112.286C47.5027 112.536 47.2975 112.739 47.044 112.739H2.15966C1.90617 112.739 1.70122 112.536 1.70122 112.286V111.413C1.70122 111.157 1.9117 110.951 2.16934 110.959C4.52161 111.031 14.9241 110.46 14.9241 94.897V19.505C14.9241 1.87529 2.236 1.44815 0.257692 1.48921C0.116395 1.49195 9.3018e-06 1.3811 0 1.24149V0.247721C1.49214e-05 0.110865 0.112205 1.47299e-05 0.250715 0H48.9516ZM106.55 2.07783C105.424 2.07788 104.51 2.97991 104.51 4.09406V54.3171C104.51 82.9582 104.567 100.8 106.211 104.088C107.832 107.331 108.385 110.234 115.219 110.234C137.089 110.234 159.41 98.7605 159.41 56.4345C159.41 14.108 130.1 2.07785 119.277 2.07783H106.55Z"
                                        fill="#D9D9D9"
                                    />
                                </svg>
                            </div>

                            {/* HOME */}
                            <p
                                className="absolute z-10 whitespace-nowrap font-sans text-[1.078rem] font-medium leading-[0.9] text-white"
                                style={{ left: '64.4%', top: '49.1%' }}
                            >
                                HOME
                            </p>
                            {/* DESIGN */}
                            <p
                                className="absolute z-10 whitespace-nowrap font-sans text-[1.078rem] font-medium leading-[0.9] text-white"
                                style={{ left: '72.2%', top: '52.4%' }}
                            >
                                DESIGN
                            </p>
                            {/* EVENING */}
                            <p
                                className="absolute z-10 whitespace-nowrap font-sans text-[1.078rem] font-medium leading-[0.9] text-white"
                                style={{ left: '55.7%', top: '55.9%' }}
                            >
                                EVENING
                            </p>

                            {/* HOSTED BY */}
                            <p
                                className="absolute z-10 whitespace-nowrap font-sans text-[1.078rem] font-medium leading-[0.9] text-white"
                                style={{ left: '8.3%', top: '68.1%' }}
                            >
                                HOSTED BY
                            </p>
                            {/* TOM */}
                            <p
                                className="absolute z-10 whitespace-nowrap font-sans text-[1.078rem] font-medium leading-[1] text-white"
                                style={{ left: '28.6%', top: '71.5%' }}
                            >
                                <span>TOM</span>
                                <br />
                                <span>WEBSTER</span>
                            </p>

                            {/* BY ARCHIPRO */}
                            <p
                                className="absolute z-10 whitespace-nowrap font-sans text-[0.47rem] font-medium uppercase leading-[0.9] text-white"
                                style={{
                                    left: 'calc(50% + 2.84rem)',
                                    top: '89.75%',
                                }}
                            >
                                By ArchiPro
                            </p>

                            {/* ArchiPro logo */}
                            <div
                                className="absolute z-10"
                                style={{
                                    top: '84.65%',
                                    left: '76.46%',
                                    right: '3.55%',
                                    bottom: '6.75%',
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="81"
                                    height="60"
                                    viewBox="0 0 81 60"
                                    fill="none"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M80.4528 59.1523C76.8605 51.777 73.2727 44.4037 69.687 37.0369C69.6323 36.9153 69.5405 36.7723 69.3896 36.689C69.2388 36.6058 69.0704 36.5845 68.8715 36.5845H56.4134C63.3661 30.2911 67.8023 21.3877 68.0865 11.4707C68.1018 10.9137 67.6383 10.457 67.0655 10.457H56.7282C56.7282 24.4096 45.1403 35.7223 30.8435 35.7223H27.5923C31.6153 27.4485 35.6317 19.1747 39.6437 10.8966C39.6984 10.7664 39.7836 10.6298 39.9367 10.5487C40.0897 10.4676 40.2537 10.4591 40.4592 10.4591H56.7588L56.7435 10.4207L50.8927 0V0.00426815H29.3589C29.2124 0.00426815 29.0812 0.0149385 28.9654 0.0746927C28.8495 0.136581 28.7817 0.236883 28.738 0.33505C22.5789 13.0115 16.4133 25.705 10.2433 38.4155C6.88496 45.3257 3.52884 52.2379 0.170539 59.1523C0.128997 59.2292 0.0983878 59.3103 0.0634055 59.3978C0.0437279 59.449 0.021864 59.5023 0 59.5578H15.6152C15.7399 59.5685 15.8667 59.5407 15.9694 59.4853C16.0722 59.4276 16.1443 59.3423 16.1706 59.2462C18.2389 54.9717 20.3007 50.7163 22.3581 46.4781C22.4127 46.3607 22.4936 46.239 22.6336 46.1665C22.7735 46.0939 22.8959 46.0897 23.108 46.0897H57.5262C57.7405 46.0897 57.8477 46.0982 57.9832 46.1665C58.1188 46.2369 58.1931 46.3457 58.2521 46.4631C60.3139 50.6993 62.3691 54.9375 64.4156 59.1737C64.4571 59.2569 64.5315 59.385 64.6517 59.4511C64.772 59.5173 64.8791 59.5258 65.0671 59.5301H80.6233L80.4528 59.1481V59.1523Z"
                                        fill="#D9D9D9"
                                    />
                                </svg>
                            </div>
                        </div>
                    </ScrollReveal>
                </div>
            </HDEDesktopFrame>
            <div className="mt-40 md:h-[1px] md:bg-[#d7d7d7]"></div>
        </section>
    );
};
